import { Controller } from "@hotwired/stimulus";

// Autocomplete: a text field whose suggestions come from a JSON search endpoint
// and are links — picking one opens that page rather than filling the field in.
// Nothing here decides how a suggestion looks or what it is called: the row is a
// <template> in the view that this clones, and the type labels arrive as a
// value, so every class, icon and Turkish word stays in the ERB.
//
//   <div data-controller="autocomplete"
//        data-autocomplete-url-value="/apis/search?type=country"
//        data-autocomplete-labels-value='{"country":"Ülke"}'
//        data-action="click@window->autocomplete#dismissOnClickOutside">
//     <input data-autocomplete-target="input"
//            data-action="input->autocomplete#search focus->autocomplete#reopen keydown->autocomplete#navigate">
//     <div data-autocomplete-target="panel" hidden>
//       <div data-autocomplete-target="list"></div>
//       <p data-autocomplete-target="empty" hidden>…</p>
//     </div>
//     <template data-autocomplete-target="template">…</template>
//   </div>
//
// The row's parts are found by data-field: name, type and image are filled when
// present, placeholder is removed once a photo replaces it, and context — the
// path that places the result — is optional, so a list that only ever holds one
// kind of record can leave it out.
export default class extends Controller {
  static targets = ["input", "panel", "list", "empty", "template"];
  static values = {
    url: String,
    labels: Object,
    minimum: { type: Number, default: 2 },
    delay: { type: Number, default: 250 },
  };

  connect() {
    this.dismiss();
  }

  disconnect() {
    clearTimeout(this.timer);
    this.pending?.abort();
  }

  // Typing restarts the clock, so a phrase costs one request at the pause rather
  // than one per keystroke.
  search() {
    clearTimeout(this.timer);
    if (this.query.length < this.minimumValue) return this.dismiss();

    this.timer = setTimeout(() => this.load(this.query), this.delayValue);
  }

  async load(query) {
    // An answer to an older query would otherwise land after a newer one and
    // overwrite it.
    this.pending?.abort();
    this.pending = new AbortController();

    const url = new URL(this.urlValue, window.location.origin);
    url.searchParams.set("q", query);

    try {
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        signal: this.pending.signal,
      });
      if (!response.ok) return this.dismiss();

      const { results } = await response.json();
      this.render(results);
    } catch (error) {
      // Aborting is how a stale answer is dropped; anything else leaves the
      // field alone rather than showing a panel that explains nothing.
      if (error.name !== "AbortError") this.dismiss();
    }
  }

  render(results) {
    this.listTarget.replaceChildren(...results.map((result, index) => this.row(result, index)));
    this.emptyTarget.hidden = results.length > 0;
    this.active = -1;
    this.inputTarget.removeAttribute("aria-activedescendant");
    this.show();
  }

  row(result, index) {
    const row = this.templateTarget.content.firstElementChild.cloneNode(true);
    row.id = `${this.inputTarget.id}-option-${index}`;
    // A result with no page to open stays an anchor with no href — it reads and
    // is announced, but it does not pretend to lead anywhere.
    if (result.url) row.href = result.url;
    row.querySelector("[data-field='name']").textContent = result.name;
    row.querySelector("[data-field='type']").textContent = this.labelsValue[result.type] ?? "";

    // What kind of place it is answers half the question; where it is answers the
    // rest, and two places in the catalog share a name often enough for that to
    // matter. `repr` reads as parents then name ("Türkiye, İstanbul"), so its last
    // segment is the name already on the row and everything before it is the path.
    const context = row.querySelector("[data-field='context']");
    if (context) context.textContent = (result.repr ?? "").split(", ").slice(0, -1).join(" · ");

    // Picking a row is the end of the search, but not the end of the wait: the
    // page it opens still has to arrive. Marking the row keeps the answer on
    // screen until it does. Enter goes through here too — it opens a row by
    // clicking it — and a row with nowhere to go is never marked.
    if (result.url) {
      row.addEventListener("click", () => {
        this.rows.forEach((other) => other.removeAttribute("data-activated"));
        row.setAttribute("data-activated", "");
      });
    }

    // No photo yet: the row keeps the placeholder the view drew in its place.
    if (result.image) {
      const image = row.querySelector("[data-field='image']");
      image.src = result.image.small;
      image.srcset = `${result.image.small} 640w, ${result.image.medium} 1024w`;
      image.hidden = false;
      row.querySelector("[data-field='placeholder']").remove();
    }

    return row;
  }

  // Opens the row the keyboard is standing on, or with nothing picked the top
  // hit: the first suggestion is what searching means. A field with no answers
  // yet keeps the cursor, so a click on the button is never a dead end.
  // Returns whether a row was opened.
  submit() {
    const row = this.rows[this.active] ?? this.rows[0];
    if (row) {
      row.click();
      return true;
    }

    this.inputTarget.focus();
    return false;
  }

  navigate(event) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this.move(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        this.move(-1);
        break;
      case "Enter":
        // The field is a search box rather than a form, so Enter does what the
        // search button does.
        if (this.submit()) event.preventDefault();
        break;
      case "Escape":
        this.dismiss();
        break;
    }
  }

  move(step) {
    if (!this.rows.length) return;
    this.show();

    // From nothing selected, down starts at the top of the list and up at the
    // bottom of it; from there the ends wrap.
    this.active =
      this.active < 0
        ? step > 0
          ? 0
          : this.rows.length - 1
        : (this.active + step + this.rows.length) % this.rows.length;

    this.rows.forEach((row, index) => row.setAttribute("aria-selected", String(index === this.active)));
    const row = this.rows[this.active];
    this.inputTarget.setAttribute("aria-activedescendant", row.id);
    row.scrollIntoView({ block: "nearest" });
  }

  // Coming back to a field that already has suggestions shows them again rather
  // than making the visitor retype.
  reopen() {
    if (this.rows.length && this.query.length >= this.minimumValue) this.show();
  }

  dismissOnClickOutside(event) {
    if (!this.element.contains(event.target)) this.dismiss();
  }

  show() {
    this.panelTarget.hidden = false;
    this.inputTarget.setAttribute("aria-expanded", "true");
  }

  dismiss() {
    this.panelTarget.hidden = true;
    this.inputTarget.setAttribute("aria-expanded", "false");
    this.inputTarget.removeAttribute("aria-activedescendant");
    this.active = -1;
  }

  get rows() {
    return Array.from(this.listTarget.children);
  }

  get query() {
    return this.inputTarget.value.trim();
  }
}
